import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, Clock, ArrowRight, FileText, Download, Zap, RefreshCw, User, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIPELINE_STAGES = [
  { key: 'new', label: 'Submitted' },
  { key: 'mca_downloaded', label: 'Under Review' },
  { key: 'quick_report_sent', label: 'Report Ready' },
  { key: 'call_scheduled', label: 'In Discussion' },
  { key: 'call_done', label: 'In Discussion' },
  { key: 'detailed_report', label: 'Detailed Review' },
  { key: 'won', label: 'Converted' },
  { key: 'lost', label: 'Closed' },
];

const PIPELINE_COLORS = {
  new: 'bg-slate-100 text-slate-600',
  mca_downloaded: 'bg-blue-100 text-blue-700',
  quick_report_sent: 'bg-cyan-100 text-cyan-700',
  call_scheduled: 'bg-indigo-100 text-indigo-700',
  call_done: 'bg-violet-100 text-violet-700',
  detailed_report: 'bg-amber-100 text-amber-700',
  won: 'bg-green-100 text-green-700',
  lost: 'bg-red-100 text-red-700',
};

const STATUS_MAP = {
  running: 'Running', closed: 'Stopped', close: 'Stopped'
};
const INTENT_MAP = {
  close: 'Strike-off', dormant: 'Dormant', continue: 'Regularise'
};

// Pulse indicators based on lead stage
function getPulseInfo(lead) {
  const stage = lead.pipeline_stage || 'new';
  if (lead.quick_report_status === 'available' && stage !== 'won') {
    return { text: 'Report Ready — Share with client!', color: 'border-green-300 bg-green-50/50', dot: 'bg-green-500' };
  }
  if (stage === 'mca_downloaded') {
    return { text: 'MCA data under review by our team', color: 'border-blue-200 bg-blue-50/30', dot: 'bg-blue-500' };
  }
  if (lead.estimated_savings && lead.estimated_savings > 0 && stage === 'new') {
    return { text: 'Savings updated — check latest estimate', color: 'border-amber-200 bg-amber-50/30', dot: 'bg-amber-500' };
  }
  if (stage === 'won') {
    return { text: 'Converted to Active Engagement!', color: 'border-violet-200 bg-violet-50/30', dot: 'bg-violet-500' };
  }
  return null;
}

export default function PartnerLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('rpace_token');

  useEffect(() => {
    async function fetchLeads() {
      try {
        const res = await fetch(`${API_URL}/api/partners/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setLeads(await res.json());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    fetchLeads();
  }, [token]);

  const fmt = (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—';

  if (loading) return (
    <div className="max-w-4xl mx-auto px-6 py-12 text-center text-sm text-slate-400">Loading leads...</div>
  );

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-[#1e3a5f]" data-testid="partner-leads-title">My Pipeline</h1>
        <p className="text-xs text-slate-500 mt-1">Leads you originated. Track their progress and download reports to share with clients.</p>
      </div>

      {leads.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Building2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No leads yet. Create one from the CCFS Calculator tab.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {leads.map(lead => {
            const stage = lead.pipeline_stage || 'new';
            const stageInfo = PIPELINE_STAGES.find(s => s.key === stage) || PIPELINE_STAGES[0];
            const stageIdx = PIPELINE_STAGES.findIndex(s => s.key === stage);
            const statusLabel = STATUS_MAP[lead.quiz_answers?.[0]] || '—';
            const intentLabel = INTENT_MAP[lead.quiz_answers?.[2]] || '—';

            return (
              <Card key={lead.id} className="overflow-hidden" data-testid={`partner-lead-${lead.id}`}>
                <CardContent className="p-0">
                  {/* Pulse Indicator */}
                  {(() => {
                    const pulse = getPulseInfo(lead);
                    if (!pulse) return null;
                    return (
                      <div className={`px-4 py-2 flex items-center gap-2 border-b ${pulse.color}`} data-testid={`pulse-${lead.id}`}>
                        <span className={`w-2 h-2 rounded-full animate-pulse shrink-0 ${pulse.dot}`} />
                        <p className="text-xs font-medium text-slate-700">{pulse.text}</p>
                      </div>
                    );
                  })()}

                  {/* Header */}
                  <div className="p-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {lead.lead_number && <span className="text-xs font-mono text-[#1e3a5f] mr-2">{lead.lead_number}</span>}
                        {lead.company_name || 'Unnamed'}
                      </p>
                      <p className="text-xs text-slate-400 font-mono mt-0.5">{lead.cin || 'No CIN'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {lead.estimated_savings > 0 && (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                          {fmt(lead.estimated_savings)}
                        </span>
                      )}
                      <span className={`text-[10px] font-medium px-2 py-1 rounded-full ${PIPELINE_COLORS[stage]}`}>
                        {stageInfo.label}
                      </span>
                    </div>
                  </div>

                  {/* Contact Details */}
                  {(lead.name || lead.email || lead.phone) && (
                    <div className="px-4 pb-3 flex items-center gap-4 flex-wrap" data-testid={`contact-${lead.id}`}>
                      {lead.name && (
                        <span className="flex items-center gap-1 text-xs text-slate-600">
                          <User className="w-3 h-3 text-slate-400" /> {lead.name}
                        </span>
                      )}
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                          <Mail className="w-3 h-3" /> {lead.email}
                        </a>
                      )}
                      {lead.phone && (
                        <a href={`tel:${lead.phone}`} className="flex items-center gap-1 text-xs text-green-600 hover:underline">
                          <Phone className="w-3 h-3" /> {lead.phone}
                        </a>
                      )}
                    </div>
                  )}

                  {/* Pipeline Progress */}
                  <div className="px-4 pb-3">
                    <div className="flex items-center gap-0.5">
                      {PIPELINE_STAGES.map((s, idx) => {
                        const isPast = idx < stageIdx;
                        const isActive = idx === stageIdx;
                        return (
                          <div key={s.key} className="flex items-center flex-1">
                            <div className={`h-1.5 w-full rounded-full transition-all ${
                              isPast ? 'bg-[#1e3a5f]' : isActive ? 'bg-[#1e3a5f]/60' : 'bg-slate-200'
                            }`} />
                            {idx < PIPELINE_STAGES.length - 1 && <div className="w-0.5 shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-[9px] text-slate-400">New</span>
                      <span className="text-[9px] text-slate-400">Converted</span>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="border-t border-slate-100 px-4 py-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Status</p>
                      <p className="text-xs font-medium text-slate-700">{statusLabel}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Intent</p>
                      <p className="text-xs font-medium text-slate-700">{intentLabel}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Quick Report</p>
                      <p className="text-xs font-medium">
                        {lead.quick_report_status === 'available' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[11px] px-2 gap-1 border-green-300 text-green-700 hover:bg-green-50"
                            data-testid={`download-report-${lead.id}`}
                            onClick={() => {
                              const token = localStorage.getItem('rpace_token');
                              fetch(`${API_URL}/api/ccfs-leads/${lead.id}/quick-report/pdf`, {
                                headers: { Authorization: `Bearer ${token}` }
                              })
                                .then(r => r.blob())
                                .then(blob => {
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `Quick_Report_${lead.company_name || 'Company'}.pdf`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                });
                            }}
                          >
                            <Download className="w-3 h-3" /> Download Report
                          </Button>
                        ) : lead.quick_report_status === 'being_generated' ? (
                          <span className="text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" /> Being Generated</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between">
                    <span className="text-[10px] text-slate-400">Created {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</span>
                    {stage === 'won' && (
                      <span className="text-[10px] font-medium text-green-600 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Converted to Case</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
